import axios from "axios";
import { LOGIN_REQUEST, LOGIN_SUCCESS, LOGIN_ERROR, LOGOUT, MFA_REQUIRED } from "../types";
//import jwt_decode from "jwt-decode";
import { AAS_PREFIX, JWT_TOKEN } from "../../global/constants";
import setJWTToken from "../../securityUtils/setJWTToken";
import jwtDecode from "jwt-decode";
import { clearAllSessionData } from "../../securityUtils/cookieUtils";
import { clearAllPersistedJobs } from "../ws/JobPersistenceService";
import {
  clearSessionScopedStorage,
  clearUserScopedStorage,
} from "../../global/storageScopePolicy";

export const loginRequest = () => ({ type: LOGIN_REQUEST });
export const loginSuccess = (data) => ({
  type: LOGIN_SUCCESS,
  payload: data,
});
export const loginError = (err) => ({
  type: LOGIN_ERROR,
  payload: err,
});

// Wipe every trace of the current user before a logout / user switch: the axios
// auth header, the identity localStorage keys, plus cookies & sessionStorage.
// The Redux store itself is reset by the LOGOUT action (see store/reducer).
//
// "username" and project-selection filters are intentionally NOT cleared here.
// They survive explicit logout so that a same-user re-login immediately recovers
// context. login() clears them only when switching to a different user.
const clearSession = () => {
  setJWTToken(null);
  clearAllSessionData();
  // Progress-card buckets are owned by JobPersistenceService; every other
  // credential / tenant-bound key is cleared via the storage-scope policy.
  clearAllPersistedJobs();
  clearSessionScopedStorage();
};

// Clear per-user preferences/filters and identity — called when switching to a
// different user to prevent data leakage across accounts. (`username` survives a
// same-user logout for switch-detection, so it's removed explicitly here.)
const clearUserContextOnSwitch = () => {
  localStorage.removeItem("username");
  clearUserScopedStorage();
};

export const logout = () => {
  clearSession();
  return { type: LOGOUT };
};

export const login = (LoginRequest) => async (dispatch) => {
  // "username" survives explicit logout (clearSession leaves it) so we can
  // detect a user switch here and wipe context that must not leak across accounts.
  const previousUsername = localStorage.getItem("username");
  const isSameUser = previousUsername && previousUsername === LoginRequest.username;

  dispatch(logout());

  if (!isSameUser) {
    clearUserContextOnSwitch();
  }

  dispatch(loginRequest());

  try {
    const res = await axios.post(`${AAS_PREFIX}/authenticate`, LoginRequest);
    const token = res.data.jwt;

    const decoded = jwtDecode(token); // { sub: "...", mfa: true?, ... }

    if (decoded.mfa) {
      dispatch({ type: MFA_REQUIRED, payload: token });
      return "MFA";
    }

    localStorage.setItem("username", LoginRequest.username);
    localStorage.setItem(JWT_TOKEN, token);
    setJWTToken(token);
    dispatch(loginSuccess());
    return "SUCCESS";
  } catch (err) {
    const errorMsg =
      err.response && err.response.status === 429
        ? "Too many connection attempts. Please try again later."
        : "Bad credentials. Please try again.";
    dispatch(loginError(errorMsg));
    return "ERROR";
  }
};

export const loginWithToken = (token) => async (dispatch) => {
  // SSO entry point — same clean-slate + user-switch semantics as password
  // login. Skipping the "username" bookkeeping here made every SSO re-login
  // look like a user switch, wiping the USER_BOUND preferences (column
  // layouts, filters) on each session.
  const previousUsername = localStorage.getItem("username");
  let username;
  try {
    username = jwtDecode(token)?.sub;
  } catch (err) {
    username = undefined;
  }
  const isSameUser =
    previousUsername && username && previousUsername === username;

  dispatch(logout());

  if (!isSameUser) {
    clearUserContextOnSwitch();
  }

  dispatch(loginRequest());
  try {
    if (username) localStorage.setItem("username", username);
    localStorage.setItem(JWT_TOKEN, token);
    setJWTToken(token);
    dispatch(loginSuccess());
  } catch (err) {
    dispatch(loginError("SSO login failed"));
  }
};

export const verifyMfaCode = (code) => async (dispatch, getState) => {
  const tempToken = getState().security.tempToken;
  try {
    const res = await axios.post(`${AAS_PREFIX}/mfa/verify`, { code }, {
      headers: {
        Authorization: `Bearer ${tempToken}`,
      },
    });
    const finalToken = res.data.jwt;
    localStorage.setItem(JWT_TOKEN, finalToken);
    setJWTToken(finalToken);
    dispatch({ type: LOGIN_SUCCESS });
  } catch (err) {
    dispatch({ type: LOGIN_ERROR, payload: "Invalid MFA code." });
  }
};


export const sendPasswordRecoveryEmail = (email) => async (dispatch) => {
  try {
    await axios.post(`${AAS_PREFIX}/users/forgot-password?email=${email}`);
    return true;
  } catch (err) {
    return false;
  }
};

export const resetPassword = (request) => async (dispatch) => {
  try {
    await axios.post(`${AAS_PREFIX}/users/reset-password`, request);
    return true;
  } catch (err) {
    return false;
  }
};
